import { Grid, Typography } from '@material-ui/core';
import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AppState } from 'src/common/redux/configure-store';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import SalesKeypad from 'src/sales-keypad/components/sales-keypad';
import { codeContainerStyles } from './code-container-styles';
import { getIsManualWeightDisabled } from 'src/common/device/scale/scale-helper';
import TrainingModeWatermark from 'src/common/training-mode/training-mode-watermark';

type Props = ICodeContainerProps;

const ProductCodeContainerKeypad: React.FC<Props> = ({
  onKeypadSubmit,
  keypadComponent,
}): JSX.Element => {
  const { t } = useTranslation();
  const classes = codeContainerStyles();
  const returnMode = useSelector(
    (state: AppState): boolean => !!state.posMode.returnMode.returnType,
  );
  const scale = useSelector((state: AppState) => state.scale);
  return (
    <Grid item className={classes.keypadContainer}>
      {!keypadComponent ? (
        <SalesKeypad
          keyboardId='code-container'
          keyPadSubmit={onKeypadSubmit}
          isKeypadVisible
          topLabelText={t('Keypad_Label_Sale_Mode')}
          dataTestIdModifier='code'
          disableManualWeight={getIsManualWeightDisabled(returnMode, scale)}
        />
      ) : (
        keypadComponent
      )}
    </Grid>
  );
};

export const ProductCodeContainer: React.FC<Props> = (props: Props): JSX.Element => {
  const classes = codeContainerStyles();
  const trainingModeClasses = trainingModeStyles();
  const isLeftHanded = useSelector(
    (state: AppState): boolean => state.cashierPreferences.leftHanded,
  );
  const isTrainingMode = useSelector((state: AppState): boolean => state.posMode.trainingMode);

  return (
    <Grid item className={`fullHeight flexFull`} style={{ width: '0' }} data-testid='code'>
      {isTrainingMode && (
        <TrainingModeWatermark supplementalStyles={`${trainingModeClasses.centerTradeWaterMark}`} />
      )}
      <Grid item className={`${classes.background} fullWidth fullHeight`}>
        <Grid
          container
          direction='column'
          style={{ flex: '1 1 0' }}
          className={`fullHeight ${classes.codeContainer} ${
            isTrainingMode ? trainingModeClasses.trainingModeBackground : ''
          }`}
          data-testid='code-container'
        >
          <Grid
            item
            container
            className={`${classes.fitHeight} ${
              props.titleSubContainerComponent || props.headerSubTitle
                ? classes.codeContainerSubContainer
                : {}
            }`}
            data-testid='code-container-subContainer'
          >
            <Grid
              container
              direction='column'
              style={
                props.titleSubContainerComponent || props.headerSubTitle
                  ? { flex: 3, display: 'inline' }
                  : {}
              }
            >
              <Grid item>
                <Typography
                  variant='h2'
                  className={classes.codeContainerTitleHeader}
                  data-testid='code-container-title-text'
                >
                  {props.headerTitle}
                </Typography>
              </Grid>
              <Grid item xs={1}>
                <Typography className={`${classes.codeContainerTitleBar}`} />
              </Grid>
              {props.headerSubTitle && (
                <Grid item>
                  <Typography
                    variant='h5'
                    className={classes.subTitleBottle}
                    data-testid='code-container-sub-title-text'
                  >
                    {props.headerSubTitle}
                  </Typography>
                </Grid>
              )}
            </Grid>
            {props.titleSubContainerComponent && (
              <Grid item container className={classes.amountContainer}>
                <Grid item className={classes.amount}>
                  {props.titleSubContainerComponent}
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid item container style={{ flex: 1 }}>
            {isLeftHanded && <ProductCodeContainerKeypad {...props} />}
            {props.children}
            {!isLeftHanded && <ProductCodeContainerKeypad {...props} />}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export interface ICodeContainerProps {
  children: React.ReactNode;
  onKeypadSubmit: (itemId: string) => void;
  keypadComponent?: JSX.Element;
  headerTitle: string;
  headerSubTitle?: string;
  titleSubContainerComponent?: JSX.Element;
}
export default ProductCodeContainer;
